import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type AudioTranscription, FilesApiService } from '../../../../core/api/files-api.service';
import { UiButton, UiSkeletonRow } from '../../../../shared/ui';

type TranscriptionState = 'idle' | 'loading' | 'success' | 'empty' | 'error' | 'insufficient_credit';

/** Poll cadence and cap while the server transcribes (status=pending). */
const POLL_INTERVAL_MS = 1500;
const MAX_POLLS = 20; // ~30s ceiling so a stuck job doesn't poll forever.

/**
 * WhatsApp-style "Transcrever" affordance shown under an audio message. On first
 * expand it fetches the automatic transcription; while the server is still working
 * (status=pending) it polls until a terminal state. Every state is handled: loading,
 * success (with an "automatic — may contain errors" label), no-speech, and error
 * (with a retry). The transcript is AI-generated, made explicit in the UI.
 */
@Component({
  selector: 'app-audio-transcription',
  imports: [UiButton, UiSkeletonRow, RouterLink],
  templateUrl: './audio-transcription.html',
  styleUrl: './audio-transcription.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioTranscriptionComponent {
  private readonly filesApi = inject(FilesApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly fileId = input.required<string>();

  protected readonly state = signal<TranscriptionState>('idle');
  protected readonly text = signal<string>('');
  protected readonly expanded = signal(false);

  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private polls = 0;

  constructor() {
    this.destroyRef.onDestroy(() => this.clearPoll());
  }

  /** Toggle the panel; fetch on first open (or after an error). */
  protected onToggle(): void {
    if (this.expanded()) {
      this.expanded.set(false);
      this.clearPoll();
      // A cut-short loading state must not stick, or reopening would show a
      // frozen skeleton (the fetch guard below skips non-idle/error states).
      if (this.state() === 'loading') this.state.set('idle');
      return;
    }
    this.expanded.set(true);
    if (this.state() === 'idle' || this.state() === 'error') {
      this.fetch(false);
    }
  }

  protected onRetry(): void {
    this.fetch(true);
  }

  private fetch(retry: boolean): void {
    this.clearPoll();
    this.polls = 0;
    this.state.set('loading');
    this.request(this.fileId(), retry);
  }

  /** Captures fileId for the whole request/poll cycle so a later input change
   *  can't make a poll overwrite state with another file's transcript. */
  private request(fileId: string, retry: boolean): void {
    this.filesApi
      .getTranscription(fileId, retry)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => this.apply(fileId, result),
        error: () => this.state.set('error'),
      });
  }

  private apply(fileId: string, result: AudioTranscription): void {
    switch (result.status) {
      case 'succeeded':
        this.text.set(result.text ?? '');
        this.state.set('success');
        break;
      case 'empty':
        this.state.set('empty');
        break;
      case 'pending':
        this.schedulePoll(fileId);
        break;
      case 'failed':
        this.state.set('error');
        break;
      case 'insufficient_credit':
        this.state.set('insufficient_credit');
        break;
    }
  }

  private schedulePoll(fileId: string): void {
    this.state.set('loading');
    if (this.polls >= MAX_POLLS) {
      this.state.set('error');
      return;
    }
    this.polls += 1;
    this.pollTimer = setTimeout(() => this.request(fileId, false), POLL_INTERVAL_MS);
  }

  private clearPoll(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
